/**
 * ManageDashboard - Overview cards for Manage section
 *
 * Shows quick stats and navigation to each sub-section.
 * Cards are filtered based on user role.
 */

import { useEffect, useState } from 'react';
import {
  fetchTrainingStats,
  fetchTeams,
  fetchPersons,
  fetchUsers,
  fetchFtpSources,
  fetchFtpTargets,
  fetchMatches,
  fetchPhotoStats,
} from '../../../../api';
import Spinner from '../../../../shared/components/ui/Spinner';
import styles from './ManageDashboard.module.css';

function StatCard({ label, value, subtext, variant }) {
  return (
    <div className={`${styles.statCard} ${variant ? styles[variant] : ''}`}>
      <div className={styles.statValue}>{value ?? '-'}</div>
      <div className={styles.statLabel}>{label}</div>
      {subtext && <div className={styles.statSubtext}>{subtext}</div>}
    </div>
  );
}

function DashboardCard({ title, stats, description, onClick, disabled }) {
  return (
    <button
      className={styles.card}
      onClick={onClick}
      disabled={disabled}
    >
      <h3 className={styles.cardTitle}>{title}</h3>
      {stats && <div className={styles.cardStats}>{stats}</div>}
      {description && <p className={styles.cardDescription}>{description}</p>}
    </button>
  );
}

export default function ManageDashboard({
  onNavigate,
  canManagePersons = false,
  canManageCompany = false,
}) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    training: null,
    teams: null,
    persons: null,
    users: null,
    ftpSources: null,
    ftpTargets: null,
    matches: null,
    photos: null,
  });

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          fetchTrainingStats(),
          canManagePersons ? fetchTeams({ limit: 1 }) : Promise.resolve(null),
          canManagePersons ? fetchPersons({ limit: 1 }) : Promise.resolve(null),
          canManageCompany ? fetchUsers({ limit: 1 }) : Promise.resolve(null),
          canManageCompany ? fetchFtpSources({ limit: 1 }) : Promise.resolve(null),
          canManageCompany ? fetchFtpTargets({ limit: 1 }) : Promise.resolve(null),
          canManagePersons ? fetchMatches({ limit: 1 }) : Promise.resolve(null),
          fetchPhotoStats(),
        ]);

        setStats({
          training: results[0].status === 'fulfilled' ? results[0].value : null,
          teams: results[1].status === 'fulfilled' ? results[1].value : null,
          persons: results[2].status === 'fulfilled' ? results[2].value : null,
          users: results[3].status === 'fulfilled' ? results[3].value : null,
          ftpSources: results[4].status === 'fulfilled' ? results[4].value : null,
          ftpTargets: results[5].status === 'fulfilled' ? results[5].value : null,
          matches: results[6].status === 'fulfilled' ? results[6].value : null,
          photos: results[7].status === 'fulfilled' ? results[7].value : null,
        });
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [canManagePersons, canManageCompany]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const trainingPending = stats.training?.by_status?.ready || 0;
  const teamCount = stats.teams?.count ?? stats.teams?.results?.length ?? 0;
  const personCount = stats.persons?.count ?? 0;
  const userCount = stats.users?.count ?? stats.users?.results?.length ?? 0;
  const ftpSourceCount = stats.ftpSources?.count ?? stats.ftpSources?.results?.length ?? 0;
  const ftpTargetCount = stats.ftpTargets?.count ?? stats.ftpTargets?.results?.length ?? 0;
  const matchCount = stats.matches?.count ?? stats.matches?.results?.length ?? 0;

  // Photo stats
  const photoTotal = stats.photos?.total ?? 0;
  const photoDone = stats.photos?.done ?? 0;
  const photoPending = stats.photos?.pending ?? 0;
  const photoProcessing = (stats.photos?.processing ?? 0) +
    (stats.photos?.detecting ?? 0) +
    (stats.photos?.identifying ?? 0);

  // Training stats
  const labeledFaces = stats.training?.faces?.labeled ?? 0;
  const unlabeledFaces = stats.training?.faces?.unlabeled ?? 0;
  const totalFaces = stats.training?.faces?.total ?? 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Management Dashboard</h2>
        <p className={styles.subtitle}>Configure teams, persons, and company settings</p>
      </div>

      {/* Overview Stats */}
      <div className={styles.statsSection}>
        <h3 className={styles.sectionTitle}>:: System Overview</h3>
        <div className={styles.statsGrid}>
          <StatCard
            label="Total Photos"
            value={photoTotal.toLocaleString()}
            variant="total"
          />
          <StatCard
            label="Completed"
            value={photoDone.toLocaleString()}
            variant="done"
          />
          <StatCard
            label="Processing"
            value={photoProcessing.toLocaleString()}
            variant="processing"
          />
          <StatCard
            label="Pending Review"
            value={photoPending.toLocaleString()}
            variant="pending"
          />
        </div>
      </div>

      {/* Navigation Cards */}
      <div className={styles.cardsSection}>
        <h3 className={styles.sectionTitle}>:: Quick Access</h3>
        <div className={styles.grid}>
          {/* Training - visible to all */}
          <DashboardCard
            title="Training"
            stats={trainingPending > 0 ? `${trainingPending} pending` : 'Up to date'}
            description="Label faces for recognition"
            onClick={() => onNavigate('/manage/training')}
          />

          {/* Teams - manager/planner only */}
          {canManagePersons && (
            <DashboardCard
              title="Teams"
              stats={`${teamCount} teams`}
              description="Manage team aliases"
              onClick={() => onNavigate('/manage/teams')}
            />
          )}

          {/* Persons - manager/planner only */}
          {canManagePersons && (
            <DashboardCard
              title="Persons"
              stats={`${personCount}+ persons`}
              description="Manage person aliases, merge duplicates"
              onClick={() => onNavigate('/manage/persons')}
            />
          )}

          {/* Matches - manager/planner only */}
          {canManagePersons && (
            <DashboardCard
              title="Matches"
              stats={`${matchCount} matches`}
              description="Upcoming and past events"
              onClick={() => onNavigate('/matches')}
            />
          )}

          {/* Company - manager only */}
          {canManageCompany && (
            <DashboardCard
              title="Company"
              stats={`${userCount} users`}
              description="Users, roles, FTP, profiles"
              onClick={() => onNavigate('/manage/company')}
            />
          )}
        </div>
      </div>

      {/* Face Recognition Stats */}
      <div className={styles.statsSection}>
        <h3 className={styles.sectionTitle}>:: Face Recognition</h3>
        <div className={styles.statsGrid}>
          <StatCard
            label="Labeled Faces"
            value={labeledFaces.toLocaleString()}
            variant="done"
          />
          <StatCard
            label="Unlabeled Faces"
            value={unlabeledFaces.toLocaleString()}
            variant="pending"
          />
          <StatCard
            label="Total Faces"
            value={totalFaces.toLocaleString()}
            variant="total"
          />
          <StatCard
            label="Known Persons"
            value={personCount.toLocaleString()}
          />
        </div>
      </div>

      {/* Company Stats for managers */}
      {canManageCompany && (
        <div className={styles.statsSection}>
          <h3 className={styles.sectionTitle}>:: Company Resources</h3>
          <div className={styles.statsGrid}>
            <StatCard
              label="Users"
              value={userCount}
            />
            <StatCard
              label="FTP Sources"
              value={ftpSourceCount}
              subtext="Photographer uploads"
            />
            <StatCard
              label="FTP Targets"
              value={ftpTargetCount}
              subtext="Output destinations"
            />
            <StatCard
              label="Teams"
              value={teamCount}
            />
          </div>
        </div>
      )}
    </div>
  );
}
